
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      async refreshCartCount() {
        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });

          if (!response.ok) throw new Error('Cart fetch failed');

          const cartData = await response.json();
          const cartLink = document.querySelector('#cart-icon-bubble');

          if (!cartLink) return;

          let bubble = cartLink.querySelector('.cart-count-bubble');

          if (cartData.item_count > 0) {
            if (!bubble) {
              bubble = document.createElement('div');
              bubble.className = 'cart-count-bubble';
              cartLink.appendChild(bubble);
            }

            bubble.replaceChildren();

            if (cartData.item_count < 100) {
              const visibleCount = document.createElement('span');
              visibleCount.setAttribute('aria-hidden', 'true');
              visibleCount.textContent = cartData.item_count;
              bubble.appendChild(visibleCount);
            }

            const hiddenCount = document.createElement('span');
            hiddenCount.className = 'visually-hidden';
            hiddenCount.textContent =
              `Cart contains ${cartData.item_count} items`;
            bubble.appendChild(hiddenCount);
          } else if (bubble) {
            bubble.remove();
          }
        } catch (error) {
          console.error('Cart count update failed:', error);
        }
      }

      async refreshCartDrawer() {
        try {
          const response = await fetch(
            `${window.location.pathname}?sections=cart-drawer`,
            {
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
              cache: 'no-store',
            }
          );

          if (!response.ok) throw new Error('Drawer refresh failed');

          const sections = await response.json();
          const html = sections['cart-drawer'];

          if (!html) return;

          const parsed = new DOMParser().parseFromString(
            html,
            'text/html'
          );

          const newDrawer = parsed.querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');

          if (!newDrawer || !currentDrawer) return;

          const drawer = document.querySelector('cart-drawer');
          const wasOpen =
            drawer?.classList.contains('active') ||
            currentDrawer.classList.contains('active');

          currentDrawer.innerHTML = newDrawer.innerHTML;

          if (wasOpen) {
            drawer?.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }
        } catch (error) {
          console.error('Cart drawer refresh failed:', error);
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (
          this.submitButton?.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.handleErrorMessage();

        this.submitButton?.setAttribute('aria-disabled', 'true');
        this.submitButton?.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const variantId = formData.get('id');

        if (this.cart) {
          const sections = this.cart
            .getSectionsToRender()
            .map((section) => section.id);

          formData.set('sections', sections.join(','));
          formData.set('sections_url', window.location.pathname);

          this.cart.setActiveElement?.(document.activeElement);
        }

        config.body = formData;

        let cartUpdated = false;

        try {
          const response = await fetch(routes.cart_add_url, config);
          const data = await response.json();

          if (data.status) {
            this.handleErrorMessage(
              data.description || data.message || 'Unable to add this item.'
            );

            // Stock-limit errors may still have added available stock.
            await this.refreshCartCount();
            await this.refreshCartDrawer();
            cartUpdated = true;
            return;
          }

          if (!this.cart) {
            window.location.href = routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: data,
          });

          if (typeof this.cart.renderContents === 'function') {
            this.cart.renderContents(data);
          }

          // Fetch the actual cart after Shopify processes the add request.
          await this.refreshCartCount();
          await this.refreshCartDrawer();

          // Open drawer after updating its contents.
          if (
            this.cart.tagName?.toLowerCase() === 'cart-drawer' &&
            typeof this.cart.open === 'function'
          ) {
            this.cart.open(this.submitButton);
          }

          cartUpdated = true;
        } catch (error) {
          console.error('Add to cart failed:', error);
          this.handleErrorMessage(
            error.message || 'Unable to add item to cart.'
          );

          await this.refreshCartCount();
        } finally {
          this.submitButton?.classList.remove('loading');
          this.submitButton?.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');

          if (cartUpdated) {
            this.cart?.classList.remove('is-empty');
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        const wrapper = this.querySelector(
          '.product-form__error-message-wrapper'
        );

        if (!wrapper) return;

        const message = wrapper.querySelector(
          '.product-form__error-message'
        );

        wrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && message) {
          message.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');

          if (text && this.submitButtonText) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');

          if (this.submitButtonText && window.variantStrings) {
            this.submitButtonText.textContent =
              window.variantStrings.addToCart;
          }
        }
      }
    }
  );
}